import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { AdminShell } from '@/app/AdminShell';
import { RequireStaff } from '@/app/RequireStaff';
import { AuthProvider } from '@/features/auth/useStaffAuth';
import { Clearances } from '@/routes/Clearances';
import { Commissioning } from '@/routes/Commissioning';
import { Conversation } from '@/routes/Conversation';
import { Devices } from '@/routes/Devices';
import { Installations } from '@/routes/Installations';
import { Plans } from '@/routes/Plans';
import { GlobalLive } from '@/routes/GlobalLive';
import { ZoneLibrary } from '@/routes/ZoneLibrary';
import { Sims } from '@/routes/Sims';
import { Staff } from '@/routes/Staff';
import { Tenants } from '@/routes/Tenants';
import { Vehicles } from '@/routes/Vehicles';
import { WhatsApp } from '@/routes/WhatsApp';

/**
 * Raad admin portal — `admin.raadtelematics.com`.
 *
 * WHAT SHIPS: the origin and staff sign-in (slice 17), the tenants list
 * (slice 17b, canvas `1t`), provisioning (18/18b), and plans & billing
 * (slice 20, canvas `5f`).
 *
 * WHAT IS ABSENT, AND WHY IT IS ABSENT RATHER THAN DISABLED:
 *
 *   - **The business overview (canvas 1s).** Deferred by Mahad, 25 Aug, on a
 *     measurement: it draws twelve figures and SEVEN have no data behind them —
 *     no trial status, no tenant lifecycle history, no MRR time series, no
 *     inventory table. The five that are real would render "MRR $6.00" and
 *     "2 tenants". It lands when there is a business to overview.
 *   - **city / type columns.** No such columns exist.
 *   - **Invoices, amounts owed, payment reminders.** `invoices` holds zero rows
 *     and nothing writes to it; there is no SMS/WhatsApp/EVC+ channel. Phase 3B.
 *     Slice 20 ships the parts of `5f` that are real and no control for the rest.
 *   - **Installations and the command console.** Slice 30 and slice 14.
 *
 * COMMISSIONING, ASSIGNMENT, GO-LIVE AND THE GLOBAL VEHICLE LIST LANDED IN
 * SLICE 18f — a tracker can now be registered with Traccar and watched reporting
 * BEFORE it is sold, sold into a tenant from the bench, and put live from any of
 * three screens instead of only from a wizard that had to still be open.
 *
 * DEVICES & SIMS LANDED IN SLICE 18d — the two registers, and the provisioning
 * wizard rewired to pick a tracker out of them instead of taking a typed IMEI.
 *
 * ROUTING ARRIVED WITH THE SECOND SCREEN. `try_files {path} /index.html` on the
 * `admin.` vhost means a deep link like `/plans` reaches the SPA, so this is a
 * BrowserRouter rather than a hash router.
 */
/**
 * The query client lives HERE rather than in `main.tsx`.
 *
 * `Tenants` calls `useQuery`, so an `<App />` without a provider throws on
 * mount. Keeping the provider in the entry file meant the component tree was
 * only mountable via that one path — every test had to remember to wrap it, and
 * forgetting produced a failure ("Sign out not found") that pointed at the shell
 * rather than at the missing provider. Self-contained is the shape that cannot
 * be got wrong.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 30_000, retry: 2 },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <RequireStaff>
            <AdminShell>
              <Routes>
                <Route path="/plans" element={<Plans />} />
                <Route path="/live" element={<GlobalLive />} />
                <Route path="/zone-library" element={<ZoneLibrary />} />
                {/* Super-admin only. The route is always registered; the Staff
                    component itself redirects a non-super-admin to the list, so
                    a deep link cannot dangle a screen the API would 403. */}
                <Route path="/staff" element={<Staff />} />
                {/* Slice 18f. STAFF-level: no IMEI on its wire, which is what
                    lets a support hire read it. */}
                <Route path="/vehicles" element={<Vehicles />} />
                {/* Slice 30. STAFF-level, like Vehicles: the install register's
                    wire carries no IMEI, so a support hire reads it. */}
                <Route path="/installations" element={<Installations />} />
                {/* The commissioning bench (18f) and the device & SIM registers
                    (18d). Super-admin only, like Staff: their routes are all
                    `requireSuperAdmin` and their rows carry IMEIs, so the
                    screens redirect a non-super-admin rather than rendering a
                    page whose every fetch 403s. */}
                <Route path="/commissioning" element={<Commissioning />} />
                {/* Slice 14c. Super-admin only, like the bench: its fetch is
                    `requireSuperAdmin` and its rows carry IMEIs, so the screen
                    redirects a non-super-admin rather than rendering a page whose
                    fetch 403s. */}
                <Route path="/clearances" element={<Clearances />} />
                <Route path="/devices" element={<Devices />} />
                <Route path="/sims" element={<Sims />} />
                {/* Slice 70b. Super-admin only, like Staff: the route is always
                    registered and the WhatsApp component itself redirects a
                    non-super-admin to the list, so a deep link cannot dangle a
                    screen the API would 404. */}
                <Route path="/whatsapp" element={<WhatsApp />} />
                {/* Slice 70c. The conversation drill-down (reply inside the 24 h
                    window). NOT a nav item — reached by a link from the log. Super-
                    admin only, same as the log: it redirects a non-super-admin. */}
                <Route path="/whatsapp/c/:conversationId" element={<Conversation />} />
                {/* Everything else is the tenants list, including an unknown
                    path: a 404 screen in a two-page portal is a page nobody
                    needs, and landing on the list is the useful answer. */}
                <Route path="*" element={<Tenants />} />
              </Routes>
            </AdminShell>
          </RequireStaff>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
